import { Link } from 'react-router-dom';
import { can, ROLE_LABELS, type Permission, type Role } from '@gwc/shared';
import { useAuth } from '../auth/AuthProvider';

interface Tile {
  title: string;
  body: string;
  permission?: Permission;
  to?: string;
  status?: 'ready' | 'coming';
}

const TILES: Tile[] = [
  { title: 'Tastings', body: 'Run or join a live tasting, or set a new one up.', permission: 'tasting:score', to: '/tastings' },
  { title: 'The Cellar', body: 'The catalog of every wine the club has poured. Add details once; it links everywhere.', permission: 'archive:view', to: '/cellar' },
  { title: 'Tasting Archive', body: 'Browse and cross-reference four decades of past tastings.', permission: 'archive:view', to: '/archive' },
  { title: 'Members', body: 'Directory, profiles, roles, dues, and invitations.', permission: 'archive:view', to: '/members' },
  { title: 'Contribute History', body: 'Share what you remember or have on paper from past tastings — photos, spreadsheets, scorecards, stories. Nothing is required.', permission: 'history:contribute', to: '/contribute' },
  { title: 'Review Contributions', body: 'Merge what members have sent in about past tastings into the archive.', permission: 'records:approve', to: '/contribute/review' },
  { title: 'Billing', body: 'Charges, invoices, payment history, and annual dues.', permission: 'payments:manage', to: '/billing' },
  { title: 'Communications', body: 'Write to the membership — by push and email — and see exactly who received it.', permission: 'comms:send', to: '/comms' },
  { title: 'Setup', body: 'The lists behind the wine form — producers, regions, vineyards, grapes. Proofread what members added.', permission: 'lookups:manage', to: '/setup' },
  { title: 'Import Archive', body: 'Upload and keep a safe, organised record of historical spreadsheets and CSVs.', permission: 'import:run', to: '/imports' },
];

export function Club() {
  const { user } = useAuth();
  if (!user) return null;
  const visible = TILES.filter((t) => !t.permission || can(user.roles, t.permission));

  return (
    <div>
      <p className="text-xs uppercase tracking-[0.3em] text-gold-400">The Club</p>
      <h1 className="mt-2 font-display text-3xl text-cream-50">Welcome, {user.firstName}</h1>
      <p className="mt-1 text-sm text-cream-300/70">
        {(user.roles as Role[]).map((r) => ROLE_LABELS[r] ?? r).join(' · ')}
      </p>

      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((tile) => {
          const inner = (
            <>
              <div className="flex items-center justify-between">
                <h3 className="text-lg text-gold-200">{tile.title}</h3>
                {tile.status === 'coming' && (
                  <span className="rounded-full border border-gold-600/30 px-2 py-0.5 text-[10px] uppercase tracking-wide text-cream-300/50">
                    Soon
                  </span>
                )}
              </div>
              <p className="mt-2 text-sm text-cream-300/80">{tile.body}</p>
            </>
          );
          return tile.to ? (
            <Link key={tile.title} to={tile.to} className="card block transition hover:border-gold-600/40">
              {inner}
            </Link>
          ) : (
            <div key={tile.title} className="card opacity-70">
              {inner}
            </div>
          );
        })}
      </div>
    </div>
  );
}
